import { useState, useEffect } from "react";
import type { RoleDetail } from "../hooks/useRoles";
import { useUpdateRole, useDeleteRole, useAddPermission, useRemovePermission } from "../hooks/useRoles";
import { useJobTypes } from "../hooks/useJobTypes";
import { TenantCapabilityList } from "./TenantCapabilityList";
import { JobTypeChipSelector } from "./JobTypeChipSelector";
import { StagePermissionMatrix } from "./StagePermissionMatrix";
import { isTenantCapability } from "./permissions-vocab";
import { RoleUserAssignmentsPanel } from "../roles/components/RoleUserAssignmentsPanel";
import "./RoleEditor.css";

interface RoleEditorProps {
  role: RoleDetail;
  onClose: () => void;
  onRefresh: () => void;
  onDeleted: () => void;
  /** When supplied, system roles surface a "Clone to customize" CTA
   *  that hands the role back to the parent for a prefilled Create flow. */
  onClone?: (role: RoleDetail) => void;
}

/** Derive initial chip scope from existing stagePermissions. */
function computeInitialChipScope(stagePerms: RoleDetail["permissions"]): string[] {
  if (stagePerms.length === 0) return ["*"];
  const jobTypeIds = [...new Set(stagePerms.map((p) => p.jobTypeId))];
  // If all perms use the wildcard, surface the "All" chip
  if (jobTypeIds.length === 1 && jobTypeIds[0] === "*") return ["*"];
  // Filter out wildcard entries mixed in with specific ids
  const specific = jobTypeIds.filter((id) => id !== "*");
  return specific.length > 0 ? specific : ["*"];
}

export function RoleEditor({ role, onClose, onRefresh, onDeleted, onClone }: RoleEditorProps) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { update, isSubmitting } = useUpdateRole();
  const { remove } = useDeleteRole();
  const { add } = useAddPermission();
  const { remove: removePermission } = useRemovePermission();
  const { items: jobTypes } = useJobTypes();

  // Split permissions into tenant capabilities (action is a tenant capability, stage='*')
  // and everything else (stage-scoped abilities).
  const tenantPermissions = role.permissions.filter(
    (p) => isTenantCapability(p.action) && p.stage === "*",
  );
  const stagePermissions = role.permissions.filter(
    (p) => !(isTenantCapability(p.action) && p.stage === "*"),
  );

  const [scopedJobTypeIds, setScopedJobTypeIds] = useState<string[]>(() =>
    computeInitialChipScope(stagePermissions),
  );

  useEffect(() => {
    setName(role.name);
    setDescription(role.description);
    setConfirmDelete(false);
    setScopedJobTypeIds(computeInitialChipScope(
      role.permissions.filter((p) => !(isTenantCapability(p.action) && p.stage === "*")),
    ));
  }, [role.id, role.name, role.description]);

  const isDirty = name !== role.name || description !== role.description;

  const handleSave = async () => {
    if (!isDirty || isSubmitting) return;
    await update(role.id, { name: name.trim(), description: description.trim() });
    onRefresh();
  };

  const handleDelete = async () => {
    if (role.isSystemDefault) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    await remove(role.id);
    onDeleted();
  };

  const handleTenantToggle = async (capability: string, wasChecked: boolean) => {
    if (role.isSystemDefault) return;
    if (wasChecked) {
      const existing = tenantPermissions.find((p) => p.action === capability);
      if (existing) {
        await removePermission(role.id, existing.id);
        onRefresh();
      }
    } else {
      await add(role.id, { action: capability, stage: "*", jobTypeId: "*" });
      onRefresh();
    }
  };

  const handleStageToggle = async (action: string, stage: string, wasChecked: boolean) => {
    if (role.isSystemDefault) return;
    const currentJobTypeId = scopedJobTypeIds[0] ?? "*";
    if (wasChecked) {
      const existing = stagePermissions.find(
        (p) => p.action === action && p.stage === stage && p.jobTypeId === currentJobTypeId,
      );
      if (existing) {
        await removePermission(role.id, existing.id);
        onRefresh();
      }
    } else {
      await add(role.id, { action, stage, jobTypeId: currentJobTypeId });
      onRefresh();
    }
  };

  return (
    <div className="re-panel">
      <div className="re-header">
        <h3 className="re-title">
          {role.name}
          {role.isSystemDefault && <span className="re-system-badge">SYSTEM</span>}
        </h3>
        <button className="re-close" onClick={onClose} aria-label="Close">&times;</button>
      </div>

      <div className="re-body">
        {role.isSystemDefault && (
          <div
            className="re-system-banner"
            role="status"
            data-testid="system-role-banner"
          >
            <span className="re-system-banner-text">
              System role — read-only. Clone to customize.
            </span>
            {onClone && (
              <button
                type="button"
                className="rb-btn rb-btn-save re-clone-btn"
                onClick={() => onClone(role)}
              >
                Clone Role
              </button>
            )}
          </div>
        )}

        <div className="re-section">
          <div className="re-section-title">DETAILS</div>
          <div className="re-field">
            <label className="re-label" htmlFor="role-name">Name</label>
            <input
              id="role-name"
              className="rb-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={role.isSystemDefault}
            />
          </div>
          <div className="re-field">
            <label className="re-label" htmlFor="role-desc">Description</label>
            <textarea
              id="role-desc"
              className="rb-input re-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              disabled={role.isSystemDefault}
            />
          </div>
          {isDirty && (
            <div className="re-save-row">
              <button className="rb-btn rb-btn-cancel" onClick={() => { setName(role.name); setDescription(role.description); }}>
                Cancel
              </button>
              <button className="rb-btn rb-btn-save" onClick={handleSave} disabled={isSubmitting || !name.trim()}>
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>

        <TenantCapabilityList
          permissions={tenantPermissions}
          disabled={role.isSystemDefault}
          onToggle={handleTenantToggle}
        />

        <JobTypeChipSelector
          jobTypes={(jobTypes ?? []).map((jt) => ({ id: jt.id, name: jt.name }))}
          selectedIds={scopedJobTypeIds}
          hasExistingPermissions={stagePermissions.length > 0}
          onChange={setScopedJobTypeIds}
        />

        <StagePermissionMatrix
          permissions={stagePermissions}
          jobTypeId={scopedJobTypeIds[0] ?? "*"}
          disabled={role.isSystemDefault}
          onToggle={handleStageToggle}
          roleName={role.name}
        />

        <RoleUserAssignmentsPanel roleId={role.id} />

        <div className="re-meta">
          <span>{role._count.assignments} user{role._count.assignments !== 1 ? "s" : ""} assigned</span>
          <span>&middot;</span>
          <span>{role.permissions.length} permission{role.permissions.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {!role.isSystemDefault && (
        <div className="re-footer">
          <button
            className={`rb-btn ${confirmDelete ? "re-delete-confirm" : "re-delete-btn"}`}
            onClick={handleDelete}
          >
            {confirmDelete ? "Confirm Delete" : "Delete Role"}
          </button>
        </div>
      )}
    </div>
  );
}
